import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { FormControl, Validators } from '@angular/forms';
import {
  ApplicationVerifier,
  MultiFactorInfo,
  RecaptchaVerifier,
  User as FirebaseUser,
} from 'firebase/auth';
import { AuthService } from '@core/services/auth.service';
import { FirebaseInjectionHelperService } from '@core/services/firebase-injection-helper.service';
import { LoggingService } from '@core/services/logging.service';
import { MfaEnrollmentService } from '@core/services/mfa-enrollment.service';
import { mfaErrorCode, mfaErrorDisplay } from '@core/utils/mfa-error-messages';
import { environment } from 'src/environments/environment';

/**
 * EnrollMfaComponent
 *
 * Guided second-factor enrollment page (/enroll-mfa). Reached when:
 * - `security.mfaEnforcement` is 'required' and mfaEnrolledGuard redirects a
 *   signed-in user with no enrolled factors here, or
 * - the user follows the 'prompt' banner in the dashboard layout.
 *
 * Lets the user enroll either a text-message (SMS) factor — E.164 phone
 * input, invisible reCAPTCHA, SMS code confirmation — or an authenticator
 * app (TOTP) factor — QR code / manual key plus code confirmation — via the
 * shared MfaEnrollmentService. Already-enrolled factors are listed.
 *
 * Firebase requires a recent sign-in for enrollment: on
 * `auth/requires-recent-login` the page surfaces a message and offers a
 * clean sign-out so the user can sign back in and retry.
 *
 * SSR-safe: all Firebase/DOM work is guarded with isPlatformBrowser.
 */
@Component({
  selector: 'app-enroll-mfa',
  templateUrl: './enroll-mfa.component.html',
  styleUrl: './enroll-mfa.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EnrollMfaComponent implements OnInit, AfterViewInit, OnDestroy {
  private authService = inject(AuthService);
  private firebaseHelper = inject(FirebaseInjectionHelperService);
  private loggingService = inject(LoggingService);
  private mfaEnrollment = inject(MfaEnrollmentService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  /** Chosen enrollment method (null while the user is deciding). */
  method: 'phone' | 'totp' | null = null;
  /**
   * The Auth emulator does not support authenticator-app (TOTP) factors, so
   * that option is shown disabled in local emulator mode.
   */
  readonly totpAvailable = !environment.useFirebaseEmulators;
  /** Factors already enrolled on the account. */
  enrolledFactors: MultiFactorInfo[] = [];
  /** True once the SMS verification code has been sent. */
  phoneCodeSent = false;
  /** QR code URL for the in-progress TOTP enrollment. */
  totpQrCodeUrl = '';
  /** Manual-entry secret key for the in-progress TOTP enrollment. */
  totpSecretKey = '';
  /** True while a Firebase call is in flight. */
  loading = false;
  /** Inline error message. */
  errorMessage: string | null = null;
  /** Positive feedback message. */
  infoMessage: string | null = null;
  /** True when Firebase demanded a recent login (re-auth required). */
  requiresReauth = false;

  /** Phone number input, validated as E.164 (e.g. +15551234567). */
  phoneControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [
      Validators.required,
      Validators.pattern(/^\+[1-9]\d{6,14}$/),
    ],
  });

  /** 6-digit confirmation code input (SMS or authenticator app). */
  codeControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [
      Validators.required,
      Validators.pattern(/^\d{6}$/),
    ],
  });

  private recaptchaVerifier: ApplicationVerifier | null = null;

  ngOnInit(): void {
    // SSR safety: Firebase Auth state is only available in the browser.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    void this.loadCurrentUser();
  }

  /**
   * Initialize the invisible reCAPTCHA once the container element exists.
   */
  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.initializeRecaptcha();
  }

  /**
   * Clean up the reCAPTCHA verifier and any in-flight enrollment state.
   */
  ngOnDestroy(): void {
    this.mfaEnrollment.cancelEnrollment();
    if (this.recaptchaVerifier) {
      try {
        // Firebase RecaptchaVerifier has a clear() method for cleanup, but the
        // ApplicationVerifier interface doesn't expose it in the type definitions.
        // The concrete RecaptchaVerifier class does have this method according to
        // Firebase documentation: https://firebase.google.com/docs/auth/web/phone-auth
        // Using optional chaining with type assertion to safely call it.
        (this.recaptchaVerifier as any).clear?.();
        this.recaptchaVerifier = null;
      } catch (error) {
        this.loggingService.logError('Failed to clean up reCAPTCHA', error);
      }
    }
  }

  /**
   * Selects the enrollment method and resets per-method state. Starting the
   * TOTP flow immediately generates the secret and QR code.
   * @param method - 'phone' for SMS, 'totp' for authenticator app
   */
  async selectMethod(method: 'phone' | 'totp'): Promise<void> {
    this.method = method;
    this.phoneCodeSent = false;
    this.totpQrCodeUrl = '';
    this.totpSecretKey = '';
    this.errorMessage = null;
    this.infoMessage = null;
    this.codeControl.reset('');

    if (method === 'totp') {
      await this.startTotpEnrollment();
    }
  }

  /**
   * Sends the SMS verification code to the entered phone number.
   */
  async sendPhoneCode(): Promise<void> {
    if (this.phoneControl.invalid) {
      this.phoneControl.markAsTouched();
      return;
    }

    const user = this.getFirebaseUser();
    if (!user) {
      await this.router.navigate(['/login'], { replaceUrl: true });
      return;
    }
    if (!this.recaptchaVerifier) {
      this.errorMessage =
        'Security check is not ready yet. Please try again in a moment.';
      return;
    }

    this.loading = true;
    this.errorMessage = null;
    this.infoMessage = null;

    try {
      await this.mfaEnrollment.startPhoneEnrollment(
        user,
        this.phoneControl.value.trim(),
        this.recaptchaVerifier,
      );
      this.phoneCodeSent = true;
      this.infoMessage = 'Verification code sent to your phone.';
    } catch (error: any) {
      this.handleEnrollmentError(error, 'SMS enrollment start failed');
    } finally {
      this.loading = false;
    }
  }

  /**
   * Confirms the SMS code and completes phone-factor enrollment.
   */
  async confirmPhoneCode(): Promise<void> {
    if (this.codeControl.invalid) {
      this.codeControl.markAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = null;
    this.infoMessage = null;

    try {
      await this.mfaEnrollment.confirmPhoneCode(
        this.codeControl.value.trim(),
        `Phone: ${this.phoneControl.value.trim()}`,
      );
      await this.finishEnrollment();
    } catch (error: any) {
      this.handleEnrollmentError(error, 'SMS enrollment confirm failed');
    } finally {
      this.loading = false;
    }
  }

  /**
   * Confirms the authenticator-app code and completes TOTP enrollment.
   */
  async confirmTotpCode(): Promise<void> {
    if (this.codeControl.invalid) {
      this.codeControl.markAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = null;
    this.infoMessage = null;

    try {
      await this.mfaEnrollment.confirmTotpCode(
        this.codeControl.value.trim(),
        'Authenticator App',
      );
      await this.finishEnrollment();
    } catch (error: any) {
      this.handleEnrollmentError(error, 'TOTP enrollment confirm failed');
    } finally {
      this.loading = false;
    }
  }

  /**
   * Returns to the method-selection step, discarding in-flight state.
   */
  cancelMethod(): void {
    this.mfaEnrollment.cancelEnrollment();
    this.method = null;
    this.phoneCodeSent = false;
    this.totpQrCodeUrl = '';
    this.totpSecretKey = '';
    this.errorMessage = null;
    this.infoMessage = null;
    this.codeControl.reset('');
  }

  /**
   * Continues into the app once at least one factor is enrolled.
   */
  async onContinue(): Promise<void> {
    await this.router.navigate(['/logging-in'], { replaceUrl: true });
  }

  /**
   * Signs the user out (used when Firebase requires a recent login before
   * enrollment) so they can sign back in and retry.
   */
  async onSignOut(): Promise<void> {
    await this.authService.signOutUser();
    await this.router.navigate(['/login'], { replaceUrl: true });
  }

  /**
   * Resolves the signed-in user, redirecting to /login when no session
   * exists and to /verify-email when the address is unverified (Firebase
   * rejects MFA enrollment for unverified accounts).
   */
  private async loadCurrentUser(): Promise<void> {
    await this.authService.waitForAuthReady(10000);
    const user = this.getFirebaseUser();

    if (!user) {
      await this.router.navigate(['/login'], { replaceUrl: true });
      return;
    }

    if (!user.emailVerified) {
      await this.router.navigate(['/verify-email'], { replaceUrl: true });
      return;
    }

    this.enrolledFactors = this.mfaEnrollment.loadStatus(user);
  }

  /**
   * Starts TOTP enrollment: generates the secret and QR code to display.
   */
  private async startTotpEnrollment(): Promise<void> {
    const user = this.getFirebaseUser();
    if (!user) {
      await this.router.navigate(['/login'], { replaceUrl: true });
      return;
    }

    this.loading = true;
    this.errorMessage = null;

    try {
      const { secret, qrUrl } =
        await this.mfaEnrollment.startTotpEnrollment(user);
      this.totpQrCodeUrl = qrUrl;
      this.totpSecretKey = secret.secretKey;
      this.infoMessage = 'Scan the QR code with your authenticator app.';
    } catch (error: any) {
      this.handleEnrollmentError(error, 'TOTP enrollment start failed');
    } finally {
      this.loading = false;
    }
  }

  /**
   * Shared success path: refresh the factor list and continue into the app.
   */
  private async finishEnrollment(): Promise<void> {
    const user = this.getFirebaseUser();
    if (user) {
      this.enrolledFactors = this.mfaEnrollment.loadStatus(user);
    }
    await this.router.navigate(['/logging-in'], { replaceUrl: true });
  }

  /**
   * Maps enrollment errors to inline messages; `auth/requires-recent-login`
   * flips the page into re-auth mode with a sign-out call to action.
   * @param error - Raw Firebase error
   * @param context - Log context (no PII)
   */
  private handleEnrollmentError(error: any, context: string): void {
    this.loggingService.logError(`Enroll MFA: ${context}`, {
      code: mfaErrorCode(error),
    });
    const display = mfaErrorDisplay(error);
    if (display.requiresReauth) {
      this.requiresReauth = true;
    }
    this.errorMessage = display.message;
  }

  /**
   * Returns the raw Firebase Auth user (null during SSR or when signed out).
   */
  private getFirebaseUser(): FirebaseUser | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    return this.firebaseHelper.getAuth().currentUser;
  }

  /**
   * Creates the invisible reCAPTCHA verifier used for SMS enrollment.
   */
  private initializeRecaptcha(): void {
    try {
      const auth = this.firebaseHelper.getAuth();
      this.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        'enroll-mfa-recaptcha-container',
        {
          size: 'invisible',
          callback: () => {
            // reCAPTCHA solved
          },
          'expired-callback': () => {
            this.errorMessage = mfaErrorDisplay('auth/captcha-check-failed').message;
          },
        },
      );
    } catch (error) {
      this.loggingService.logError('Failed to initialize reCAPTCHA', error);
    }
  }
}
